import { Grid, Card, CardHeader, CardContent, Button, Typography } from '@mui/material';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { contractsGridColumns } from './grid-columns';
import { useFormContext } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { Contract } from '../../../models/contract';
import { ContractsDialog } from '../../contracts/contracts.dialog';
import { useFileDownload } from '../../../components/file-download';
import { UserPermissionName } from '../../../models/user-permission-name';
import { GuardedComponent } from '../../../components/guarded-component';
import { useLoadData } from '../../../hooks/useLoadData';
import { gridApiKeyStore } from '../../../api/grid-api-key-store';
import { useSaveData } from '../../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../../hooks/use-debounce';
import { GridColumnConfig, GridPreference } from '../../../models/gridCoumnConfig';
import { ApiResponse } from '../../../models/api-response';

const styling = {
  headingStyle: { fontWeight: 'bold' },
};

interface ContractForm {
  name: string;
}

export const ContractsForm = ({ name }: ContractForm) => {
  const [openDialog, setOpenDialog] = useState(false);
  const { setValue, getValues } = useFormContext();
  const { error, downloadFile, isLoading } = useFileDownload();

  const handleOpenAddContractForm = () => {
    setOpenDialog(true);
  };

  const handleCloseAddContractForm = (contacts: Contract[]) => {
    setOpenDialog(false);
    setValue(name, contacts);
  };

  const handleDownloadFile = async (data: any) => {
    downloadFile({ additionalData: data });
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Office', 'Contract'));

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredContracts, setFilteredContracts] = useState<Contract[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredContractList = (getValues(name) || []).filter((contract: any) =>
      Object.values(contract).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredContracts(filteredContractList || []);
  }, [debouncedSearchTerm, getValues(name)]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'Contract',
      pageName: 'Office',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'Contract',
      pageName: 'Office',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <Card elevation={0}>
      <CardHeader
        className="subHead"
        title={
          <Typography variant="h3" color="textPrimary" style={styling.headingStyle}>
            CONTRACTS
          </Typography>
        }
        action={
          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_CONTRACT_OFFICE]}>
            <Button className="btnLink" sx={{ p: 0, m: 0, mr: 1 }} variant="text" onClick={handleOpenAddContractForm}>
              Add New Contract
            </Button>
          </GuardedComponent>
        }
      ></CardHeader>
      <CardContent>
        <Grid container>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <SiTrackGrid
              fileTitle='office_contract'
              dataItemKey="contractID"
              columns={contractsGridColumns}
              // dataItems={getValues(name)}
              onDownload={(data: any) => {
                handleDownloadFile(data);
              }}
              actionPermissions={{
                download: UserPermissionName.DOWNLOAD_CONTRACT_OFFICE,
                export: UserPermissionName.DOWNLOAD_GRID,
              }}
              isBusy={isLoading}
              // -----------------Grid Layout-------
              dataItems={filteredContracts || []}
              reorderable
              // handleSearch={handleInputChange}
              handleSaveLayout={handleSaveLayout}
              isSaveLayout={true}
              gridConfigFromApi={gridConfig}
              handleResetLayout={handleResetLayout}
            />
          </Grid>
          {openDialog && (
            <ContractsDialog open={openDialog} onClose={handleCloseAddContractForm} contracts={getValues(name)} />
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};
